import { Tabs as TabsPrimitive } from "radix-ui";
import * as React from "react";
import { cn } from "../lib/cn";
import { AppLink } from "./link";

// Underline tabs (ui-spec §3.1): 13px, a muted count after the label, a 2px ink underline on the active tab, the
// strip on a full-width border.subtle rule.
const trigger =
  "nd-tab relative -mb-px inline-flex h-9 shrink-0 items-center gap-1.5 border-b-2 border-transparent text-ui text-fg-muted hover:text-fg-primary data-[state=active]:border-fg-primary data-[state=active]:text-fg-primary aria-[current=page]:border-fg-primary aria-[current=page]:text-fg-primary";

export function CountBadge({ count, className }: { count: React.ReactNode; className?: string }) {
  return (
    <span className={cn("inline-flex min-w-4 items-center justify-center text-small text-fg-muted tabular-nums", className)}>
      {count}
    </span>
  );
}

export interface LinkTab {
  id: string;
  label: React.ReactNode;
  href: string;
  count?: React.ReactNode;
}

/** Tabs that are saved views or routes (`?tab=`, `…/<tab>`): each tab is a real link. */
export function LinkTabs({ tabs, active, label }: { tabs: LinkTab[]; active: string; label: string }) {
  return (
    <nav aria-label={label} className="flex gap-6 overflow-x-auto border-b border-line-subtle [scrollbar-width:none]">
      {tabs.map((t) => (
        <AppLink key={t.id} href={t.href} className={trigger} aria-current={t.id === active ? "page" : undefined}>
          {t.label}
          {t.count !== undefined ? <CountBadge count={t.count} /> : null}
        </AppLink>
      ))}
    </nav>
  );
}

const segmentGroup =
  "inline-flex h-[var(--nd-control-height)] max-w-full shrink-0 items-center gap-0.5 overflow-x-auto rounded-md border border-line p-0.5 [scrollbar-width:none]";
const segment =
  "inline-flex h-full shrink-0 items-center gap-1.5 rounded-sm px-2.5 text-ui whitespace-nowrap text-fg-muted hover:text-fg-primary aria-[current=page]:bg-selected aria-[current=page]:text-fg-primary aria-pressed:bg-selected aria-pressed:text-fg-primary";

/** A page's own views as a compact segmented control (`Active 1 · History 2 · All`); each segment is a link. */
export function LinkSegments({ tabs, active, label }: { tabs: LinkTab[]; active: string; label: string }) {
  return (
    <nav aria-label={label} className={segmentGroup}>
      {tabs.map((t) => (
        <AppLink key={t.id} href={t.href} aria-current={t.id === active ? "page" : undefined} className={segment}>
          {t.label}
          {t.count !== undefined ? <CountBadge count={t.count} /> : null}
        </AppLink>
      ))}
    </nav>
  );
}

/** The same segmented control for in-page state (a chart range): each segment is a pressed-state button. */
export function ButtonSegments<T extends string>({
  options,
  value,
  onChange,
  label,
  className,
}: {
  options: { value: T; label: React.ReactNode }[];
  value: T;
  onChange: (v: T) => void;
  label: string;
  className?: string;
}) {
  return (
    <div role="group" aria-label={label} className={cn(segmentGroup, "h-[var(--nd-control-height-sm)]", className)}>
      {options.map((o) => (
        <button key={o.value} type="button" aria-pressed={o.value === value} className={cn(segment, "px-2")} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

export const Tabs = TabsPrimitive.Root;
export const TabsContent = TabsPrimitive.Content;

export function TabsList({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.List>) {
  return <TabsPrimitive.List className={cn("flex gap-5 border-b border-line-subtle", className)} {...props} />;
}

export function TabsTrigger({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  return <TabsPrimitive.Trigger className={cn(trigger, className)} {...props} />;
}
